/**
 * @name Report
 * @description Componente común para mostrar reportes
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

// Material-UI
import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableRow from '@material-ui/core/TableRow';

import { LoadingWrapper, NoRegistersFound } from '@components';
import { FiltersService } from '@services';
import { Typography } from '@styles';

import Header from './components/header';
import Menu from './components/menu';

class Report extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorMenu: null,
      columns: [],
      orderColumns: [],
      selectedRow: '',
    };
  }

  componentDidMount() {
    const { columns } = this.props;
    let visibleColumns = columns.filter((column) => column.visible);
    visibleColumns.map((column) => {
      if (!column.minWidth) {
        column.width = ''; // Default width
        column.minWidth = 150; // Default minWidth
        // return;
      }
    });

    this.setState({
      columns: visibleColumns,
      orderColumns: new Array(visibleColumns.length).fill(null).map((n, i) => i)
    });
  }

  // *************************************
  // ORDER COLUMNS
  // *************************************

  reorderColumnHandler = ({ oldIndex, newIndex }) => {
    const { orderColumns } = this.state;
    const newOrder = [...orderColumns];
    const moved = newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, moved[0]);
    this.setState({ orderColumns: newOrder });
  }

  // *************************************
  // RESIZE COLUMNS
  // *************************************

  resizeColumnHandler = (array) => {
    this.forceRender();
    this.setState({ columns: array });
  };

  // *************************************
  // VISIBLE COLUMNS
  // *************************************

  hideColumnHandler = (item) => {
    const { columns } = this.state;
    columns[columns.findIndex((column) => column.id === item.id)].visible = !item.visible;
    this.setState({ columns });
  }

  colorRow = (indexRow) => {
    const { selectedRow } = this.state;
    if (indexRow === selectedRow) {
      return { background: '#D9D9D6' };
    } else {
      return indexRow % 2 ? { background: '#F9F9F6' } : { background: '#FFFFFF' };
    }
  }

  selectRowHandler = (indexRow) => {
    this.setState({ selectedRow: indexRow });
  }

  openMenu = (event) => {
    this.setState({ anchorMenu: event.currentTarget });
  };

  closeMenu = () => {
    this.setState({ anchorMenu: null });
  }

  delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  forceRender = async () => {
    await this.delay(1);
    this.setState({});
  }

  render() {
    const {
      rows = [],
      exportToCSV,
      exportFileName
    } = this.props;

    const {
      anchorMenu,
      columns,
      orderColumns,
    } = this.state;

    return (
      <LoadingWrapper area="loading-report">
        <ReportWrapper>
          {rows.length !== 0 && (
            <BoxToolbar>
              {/* Total de registros */}
              <BoxRegisters>
                <Typography.Text>
                  {`${i18n.t('GenericMessages.total')}: `}
                  <Typography.Medium>
                    {`${rows.length} ${i18n.t('GenericMessages.registers').toLowerCase()}`}
                  </Typography.Medium>
                </Typography.Text>
              </BoxRegisters>
              {/* Menú de acciones */}
              <BoxMenu>
                <Menu
                  anchorMenu={anchorMenu}
                  columns={columns}
                  rows={rows}
                  exportToCSV={exportToCSV}
                  exportFileName={exportFileName}
                  onHideColumn={this.hideColumnHandler}
                  openMenu={this.openMenu}
                  closeMenu={this.closeMenu}
                />
              </BoxMenu>
            </BoxToolbar>
          )}

          <TableContainer id="report">
            {rows.length !== 0 && (
              <Table>
                <Header
                  columns={columns}
                  orderColumns={orderColumns}
                  onReorderColumn={this.reorderColumnHandler}
                  onResizeColumn={this.resizeColumnHandler}
                />
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={`row-${index}`}
                      style={this.colorRow(index)}
                      onClick={() => this.selectRowHandler(index)}
                    >
                      {/* Registros */}
                      {orderColumns.map((colIdx) => (
                        columns[colIdx].visible && (
                          <TableCell
                            key={columns[colIdx].id}
                            align={columns[colIdx].align}
                          >
                            {columns[colIdx].filter ? FiltersService[columns[colIdx].filter](row[columns[colIdx].id]) : row[columns[colIdx].id]}
                          </TableCell>
                        )
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {/* Not Found */}
            {rows.length === 0 && (
              <NoRegistersFound />
            )}
          </TableContainer>
        </ReportWrapper>
      </LoadingWrapper>
    );
  }
}

Report.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  // Modelo: [{ id: string, label: string, visible: boolean, filter: string/null, minWidth: int/null }
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      visible: PropTypes.bool.isRequired,
      filter: PropTypes.string,
      minWidth: PropTypes.number,
    })
  ),
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Boolean que muestra la opción de exportar a csv.
  exportToCSV: PropTypes.bool,
  // Boolean que muestra la opción de exportar a todos los registrso a Excel.
  exportAllToExcel: PropTypes.bool,
  // Boolean que muestra la opción de exportar a los registrso filtrados a Excel.
  exportFiltersToExcel: PropTypes.bool,
  // Nombre que se le da al archivo cuando se exportan los registros.
  exportFileName: PropTypes.string
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const ReportWrapper = styled.div`
  width: 100%;
`;

const BoxToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 8px 0;
`;

const BoxRegisters = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const BoxMenu = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

// TableContainer
const TableContainer = styled(MuiTableContainer)`
  width: 100%;
  overflow: auto;
  scrollbar-width: thin !important;
`;

// Table
const Table = styled(MuiTable)`
  &.MuiTable-root {
    /*table-layout: fixed;
    white-space: nowrap;
    width: 100%;*/
    min-width: 100%;
    table-layout: fixed;
    white-space: nowrap;
    
  }
`;

// TableRow
const TableRow = styled(MuiTableRow)`
  &.MuiTableRow-root {
    transition: .5s all;
  }

  &.MuiTableRow-root.MuiTableRow-hover:hover {
    background-color: ${(props) => props.theme.colors.greyHover} !important;
  }
`;

// TableBody
const TableBody = styled(MuiTableBody)``;

// TableCell
const TableCell = styled(MuiTableCell)`
  &.MuiTableCell-root {
    padding: 8px;
    font-family: 'Bryant Pro Regular';
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.4;
    letter-spacing: normal;
    vertical-align: middle;
    color: ${(props) => props.theme.colors.greyDark};
    border-bottom: 1px solid ${(props) => props.theme.colors.greyHover};

    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export default Report;
